import { useState } from "react";
import { Link, useParams } from "react-router";
import { IconChevronRight, IconPlayerPlayFilled } from "@tabler/icons-react";
import { TutorialId } from "@repo/api/tutorial";
import { paths } from "@/App";
import { Button } from "@/lib/components/Button/Button";
import { formatTutorialMeta, getTutorialOrder } from "./tutorials";
import { useTutorialVideoDialog } from "./tutorial-hooks";
import VideoThumbnail from "@/lib/components/VideoThumbnail";

const LIST_LIMIT = 4;

export default function LearnSection({
  hasCreatedContent,
  openedTutorialIds,
}: {
  hasCreatedContent: boolean;
  openedTutorialIds: TutorialId[];
}) {
  const { teamExternalId } = useParams();
  const { play, dialog } = useTutorialVideoDialog();

  // Ordered once per visit so opening a tutorial doesn't reshuffle the list.
  const [[featured, ...rest]] = useState(() =>
    getTutorialOrder({ hasCreatedContent, openedTutorialIds }),
  );

  return (
    <section className="mt-10">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-semibold">Learn Amplifiles</h2>
        <Button
          variant="ghost"
          size="sm"
          className="-mr-3 gap-1 pr-3.5 text-text-secondary"
          asChild
        >
          <Link to={paths.tutorials(teamExternalId!)}>
            View all
            <IconChevronRight stroke={2.5} />
          </Link>
        </Button>
      </div>
      <div className="flex flex-col gap-6 lg:flex-row lg:rounded-2xl lg:border lg:border-border lg:p-4">
        <button
          onClick={() => play(featured!)}
          className="group w-full shrink-0 cursor-pointer text-left lg:w-100"
        >
          <VideoThumbnail
            title={featured!.title}
            imageSrc={featured!.thumbnailSrc}
            minutes={featured!.minutes}
          />
        </button>
        <ul className="flex flex-1 flex-col">
          {rest.slice(0, LIST_LIMIT).map((tutorial) => (
            <li
              key={tutorial.id}
              className="flex-1 border-border not-first:border-t"
            >
              <button
                onClick={() => play(tutorial)}
                className="group flex size-full cursor-pointer items-center gap-3 py-2 text-left"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-background-secondary transition-colors group-hover:bg-grey-200">
                  <IconPlayerPlayFilled className="size-3" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold">
                    {tutorial.title}
                  </span>
                  <span className="block text-xs text-text-secondary">
                    {formatTutorialMeta(tutorial)}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      {dialog}
    </section>
  );
}
